// Copyright (c) Docugami, Inc. All rights reserved.

import { useHistory, useLocation } from "react-router";
import { useSelector } from "react-redux";
import { AppState } from "../store";
import { RequiredError } from "../networking/base";
import { useMemo } from "react";
import { DocSetCollectionType, WorkPathType } from "networking/WorkPathApi";
import { DocSourceId, ExplorerButtonId, ReviewDocsId } from "components/myContentExplorer/ExplorerTypes";
import { selectIsActivePaidSubscription } from "store/AccountInfoHelpers";
import { redirectToCustomerPortal } from "billing/BillingService";
import { CoachMarkId } from "coachmarks/CoachMarkTypes";
import { useAppSelector } from 'hooks/redux';
import { usePageLimits } from "./PageLimits";
import { Telemetry } from "telemetry/TelemetryManager";
import { getPageViewForReportViewMode } from "telemetry/TelemetryEvents";
import { DeveloperHubTarget } from "pages/DeveloperHubPage";
import { ReportViewContext } from "networking/reportsApi";
import { UnassignedDocSetId } from "components/file_management/file_helpers";
import { useFeatureFlag } from "./FeatureFlags";
import { FeatureFlags } from "store/featureFlags/types";

export enum NavigationType {
    ReplaceCurrentPage,
    PushNewPage
}

export const DOC_SHELL_PAGE = "document-shell";
export const WORK_PATH_PAGE = "workPath";
export const ZAPIER_WORKFLOW_PAGE = "zapier-workflow";
export const CHAT_PAGE = "chat";
export const QUERIES_PAGE = "queries";
export const QUERY_PAGE = "query";
export const SESSION_EXPIRED_PAGE = "session-expired";
export const MY_CONTENT_PAGE = "my-content";
export const ACCOUNT_PAGE = "account";
export const BILLING_PAGE = "tiers";
export const DEVELOPER_HUB_PAGE = "developer-playground";

export enum ReportViewMode {
    Document = 'Document',
    Table = 'Table',
    Abstract = 'Abstract',
    Chat = 'Chat'
}

export interface ReportQueryParams {
    documentId?: string;
    instanceId?: string;
    mode?: ReportViewMode;
    context?: ReportViewContext;
}

export interface INavigationFunctions {
    navToDocumentPage(navType: NavigationType, clusterId: string, folderId: string, chunkInstanceId?: string): void; // AA Only

    navToReportPage(navType: NavigationType, query?: ReportQueryParams): void;
    navToWorkPathPage(navType: NavigationType, workPathId?: string, documentId?: string, instanceId?: string): void;
    getWorkPathPageUrl(workPathId?: string, documentId?: string, instanceId?: string): string | undefined;
    navToNewWorkPathPage(navType: NavigationType, type: WorkPathType, docSetId?: string, collectionFilter?: DocSetCollectionType): void;
    navToChatPage(navType: NavigationType, docSetId?: string): void;

    navToHomePage(navType: NavigationType): void;
    navToDocSetPage(navType: NavigationType, docSetId: string): void;
    getDeepLinkToDocSetPage(docSetId: string): string | undefined;
    navToAddDocsPage(): void;
    navToBrowseSamplesPage(sampleSetId?: string): void;
    navToUnassignedFiles(navType: NavigationType): void;
    navToErrorFiles(navType: NavigationType): void;
    navToLocalDocs(navType: NavigationType): void;
    navToRemoteDocs(navType: NavigationType, remoteDocSourceId: DocSourceId): void;
    navToEmptyContentCoachmark(navType: NavigationType): void;
    navToOrganizationPage(): void;
    navToUnderstandDocsLandingPage(navType: NavigationType): void;
    navToWorkFlowLandingPage(navType: NavigationType): void;
    navToCreateDocsLandingPage(navType: NavigationType): void;
    navToAccountPage(navType: NavigationType): void;
    navToDeveloperHub(target?: DeveloperHubTarget): void;
    navToSubscriptionUpgradePage(): void;
    navToWordTaskpane(): void;
    getUrlForDocumentDeeplink(clusterId: string, folderId: string, chunkInstanceId?: string): string;
    goBack(): void;
    logout(): void;
}

export interface BuilderParam {
    pathPart: string;
    value: string;
}

// Helper for building a URL.
export class UrlBuilder {
    page: string;
    params: BuilderParam[] = [];
    query: BuilderParam[] = []
    orgId?: string;

    constructor(page: string) {
        this.page = page;
    }

    addOrg(orgId?: string): UrlBuilder {
        this.orgId = orgId;
        return this;
    }

    addParam(part: string, value?: string): UrlBuilder {
        if (!!value) {
            this.params.push({ pathPart: part, value: value });
        }

        return this;
    }

    addPart(part?: string): UrlBuilder {
        if (!!part) {
            this.params.push({ pathPart: part, value: '' });
        }

        return this;
    }

    addQuery(key: string, value?: string): UrlBuilder {
        if (!!value) {
            this.query.push({ pathPart: key, value: value });
        }

        return this;
    }

    getParamByPart(pathPart: string): BuilderParam | undefined {
        for (const param of this.params) {
            if (param.pathPart === pathPart) {
                return param;
            }
        }

        return undefined;
    }

    build(): string {
        let url = `/${this.page}`
        this.params.forEach(param => {
            url += `/${param.pathPart}`;
            if (!!param.value && param.value.length > 0) {
                url += `/${param.value}`;
            }
        });

        if (this.query.length > 0) {
            let isFirst = true;
            url += '?';
            this.query.forEach(param => {
                if (!isFirst) {
                    url += '&';
                }

                url += `${param.pathPart}=${encodeURIComponent(param.value)}`
                isFirst = false;
            })
        }

        if (!!this.orgId) {
            url += `#oid=${this.orgId}`;
        }

        return url;
    }
}

export const parseReportQueryParams = (search: string): ReportQueryParams => {
    const params = new URLSearchParams(search);
    const navContext: ReportQueryParams = {
        mode: ReportViewMode[params.get('mode') as keyof typeof ReportViewMode] ?? undefined,
        context: ReportViewContext[params.get('context') as keyof typeof ReportViewContext] ?? undefined,
        documentId: params.get('documentId') ?? undefined,
        instanceId: params.get('instanceId') ?? undefined
    }

    return navContext;
}

export function useNavigationService(): INavigationFunctions {
    const history = useHistory();
    const location = useLocation();
    const orgId = useSelector((state: AppState) => state.organizations.currentOrganization?.id);

    const { pageUploadDisabled } = usePageLimits()
    const isActiveSubscriber = useSelector((state: AppState) => selectIsActivePaidSubscription(state));
    const currentWorkPath = useAppSelector(state => state.workPaths.currentWorkPath);
    const demoEnabled = useFeatureFlag(FeatureFlags.DemoFeatures);

    function nav(url: string, navType: NavigationType) {
        if (navType === NavigationType.ReplaceCurrentPage) {
            history.replace(url);
        } else {
            history.push(url);
        }
    }

    function logout() {
        nav("/logout", NavigationType.ReplaceCurrentPage)
    }

    function getUrlForDocumentDeeplink(
        clusterId: string,
        folderId: string,
        chunkInstanceId?: string): string {
        if (!orgId || !clusterId || !folderId) {
            throw new RequiredError("Specified parameter missing");
        }

        let chunkInstanceUrlPart = chunkInstanceId ? `/instance/${chunkInstanceId}` : '';

        return `/${DOC_SHELL_PAGE}/cluster/${clusterId}/folder/${folderId}${chunkInstanceUrlPart}/#oid=${orgId}`;
    }

    function navToDocumentPage(
        navType: NavigationType,
        clusterId: string,
        folderId: string,
        chunkInstanceId?: string): void {
        if (!orgId || !clusterId || !folderId || !(window as any)._OfficeMode) {
            return;
        }

        const urlBuilder = new UrlBuilder(DOC_SHELL_PAGE)
            .addParam("cluster", clusterId)
            .addParam("folder", folderId)
            .addParam("instance", chunkInstanceId)
            .addOrg(orgId);

        nav(urlBuilder.build(), navType);
    }

    function navToReportPage(navType: NavigationType, query: ReportQueryParams): void {
        if (!orgId || !currentWorkPath?.id) {
            return;
        }

        const existingQuery = parseReportQueryParams(location.search);
        const previousSearchParams = new URLSearchParams(window.location.href);
        const previousQuery = parseReportQueryParams(previousSearchParams.toString());

        if (existingQuery.documentId !== query.documentId) {
            // InstanceIds don't get persisted across document IDs
            delete existingQuery.instanceId;
        }

        const targetQuery: ReportQueryParams = {
            ...existingQuery,
            ...(query || {})
        };

        if (!!targetQuery.mode && targetQuery.mode !== previousQuery.mode) {
            Telemetry.trackPageView(getPageViewForReportViewMode(targetQuery.mode));

            if (targetQuery.mode !== ReportViewMode.Document) {
                // InstanceIds are only relevant to the document view
                delete targetQuery.instanceId;
            }
        }

        const urlBuilder = new UrlBuilder(WORK_PATH_PAGE)
            .addPart(currentWorkPath.id)
            .addQuery("context", targetQuery.context)
            .addQuery("mode", targetQuery.mode)
            .addQuery("documentId", targetQuery.documentId)
            .addQuery("instanceId", targetQuery.instanceId)
            .addOrg(orgId);

        nav(urlBuilder.build(), navType);
    }

    function navToHomePage(navType: NavigationType): void {
        if (!orgId) {
            return;
        }

        nav(`/home/#oid=${orgId}`, navType);
    }

    function navToAddDocs(): void {
        pageUploadDisabled ? navToAccountPage(NavigationType.PushNewPage) : navToContentPage(NavigationType.PushNewPage, ExplorerButtonId.AddDocs);
    }

    function navToBrowseSamplesPage(sampleId?: string): void {
        navToContentPage(NavigationType.PushNewPage, ExplorerButtonId.BrowseSamples, !!sampleId ? [{ key: 'sampleSetId', value: sampleId }] : undefined);
    }

    function navToDocSetPage(navType: NavigationType, docSetId: string): void {
        navToContentPage(navType, docSetId);
    }

    function getDeepLinkToDocSetPage(docSetId: string): string | undefined {
        if (!docSetId) {
            return;
        }

        const targetId = docSetId === UnassignedDocSetId ? ReviewDocsId.Unassigned : docSetId;
        return getDeepLinkToContentPage(targetId);
    }

    function navToUnassignedFiles(navType: NavigationType): void {
        navToContentPage(navType, ReviewDocsId.Unassigned);
    }

    function navToErrorFiles(navType: NavigationType): void {
        navToContentPage(navType, ReviewDocsId.Error);
    }

    function navToLocalDocs(navType: NavigationType): void {
        navToContentPage(navType, DocSourceId.Docugami);
    }

    function navToRemoteDocsPage(navType: NavigationType, remoteDocSourceId: DocSourceId): void {
        navToContentPage(navType, remoteDocSourceId);
    }

    function navToEmptyContentCoachmark(navType: NavigationType): void {
        navToContentPage(navType, CoachMarkId.EmptyDocSets);
    }

    function getDeepLinkToContentPage(targetId?: string, query?: [{ key: string, value: string }]): string | undefined {
        if (!orgId) {
            return;
        }

        const urlBuilder = new UrlBuilder(MY_CONTENT_PAGE)
            .addPart(targetId)
            .addOrg(orgId);

        if (!!query && !!query.length) {
            query.forEach(queryItem => {
                urlBuilder.addQuery(queryItem.key, queryItem.value);
            })
        }

        return urlBuilder.build();
    }

    function navToContentPage(navType: NavigationType, targetId?: string, query?: [{ key: string, value: string }]): void {
        const url = getDeepLinkToContentPage(targetId, query);
        if (!url) {
            return;
        }

        nav(url, navType);
    }

    function navToOrganizationPage(): void {
        nav("/organization", NavigationType.ReplaceCurrentPage);
    }

    function getWorkPathPageUrl(workPathId?: string, documentId?: string, instanceId?: string): string | undefined {
        if (!orgId) {
            return;
        }

        workPathId = workPathId ?? currentWorkPath?.id;
        if (!workPathId) {
            return;
        }

        const existingQuery = parseReportQueryParams(location.search);
        const builder = new UrlBuilder(WORK_PATH_PAGE)
            .addPart(workPathId)
            .addQuery('mode', existingQuery.mode)
            .addQuery('context', existingQuery.context)
            .addQuery('documentId', !!documentId ? documentId : existingQuery.documentId)
            .addQuery('instanceId', !!instanceId ? instanceId : existingQuery.instanceId)
            .addOrg(orgId);

        return builder.build();
    }

    function navToWorkPathPage(navType: NavigationType, workPathId?: string, documentId?: string, instanceId?: string): void {
        const url = getWorkPathPageUrl(workPathId, documentId, instanceId);
        if (!url) {
            return;
        }

        nav(url, navType);
    }

    function navToCreateDocsLandingPage(navType: NavigationType): void {
        if (!orgId) {
            return;
        }

        const url = new UrlBuilder('create')
            .addOrg(orgId)
            .build();

        nav(url, navType);
    }

    function navToUnderstandDocsLandingPage(navType: NavigationType): void {
        if (!orgId) {
            return;
        }

        const url = new UrlBuilder('understand')
            .addOrg(orgId)
            .build();

        nav(url, navType);
    }

    function navToWorkFlowLandingPage(navType: NavigationType): void {
        if (!orgId) {
            return;
        }

        const url = new UrlBuilder('workflow')
            .addOrg(orgId)
            .build();

        nav(url, navType);
    }

    function navToNewWorkPathPage(navType: NavigationType, type: WorkPathType, docSetId?: string, collectionFilter?: DocSetCollectionType): void {
        if (!orgId) {
            return;
        }

        if (type === WorkPathType.ZapierWorkFlow && demoEnabled) {
            const url = new UrlBuilder(ZAPIER_WORKFLOW_PAGE)
                .addOrg(orgId)
                .build();

            nav(url, navType);
        }
        else {
            const url = new UrlBuilder(WORK_PATH_PAGE)
                .addParam("new", type.toString())
                .addOrg(orgId)
                .addQuery('docSetId', docSetId)
                .addQuery('docSetsFilter', collectionFilter)
                .build();

            nav(url, navType);
        }
    }

    function navToChatPage(navType: NavigationType, docSetId?: string): void {
        if (!orgId) {
            return;
        }

        const url = new UrlBuilder(CHAT_PAGE)
            .addQuery('docSetId', docSetId)
            .addOrg(orgId)
            .build();

        nav(url, navType);
    }

    function navToAccountPage(navType: NavigationType): void {
        if (!orgId) {
            return;
        }

        const url = new UrlBuilder(ACCOUNT_PAGE)
            .addOrg(orgId)
            .build();

        nav(url, navType);
    }

    function navToSubscriptionUpgradePage(): void {
        if (!orgId) {
            return;
        }

        if (isActiveSubscriber) {
            redirectToCustomerPortal(orgId);
        } else {
            navToBillingPage();
        }
    }

    function navToBillingPage(): void {
        if (!orgId) {
            return;
        }

        const url = new UrlBuilder(BILLING_PAGE)
            .addOrg(orgId)
            .build();

        nav(url, NavigationType.PushNewPage);
    }

    function navToWordTaskpane(): void {
        const url = new UrlBuilder('word/taskpane')
            .build();

        nav(url, NavigationType.ReplaceCurrentPage);
    }

    function navToDeveloperHub(target?: DeveloperHubTarget): void {
        if (!orgId) {
            return;
        }

        const url = new UrlBuilder(DEVELOPER_HUB_PAGE).addOrg(orgId).addPart(target).build();
        nav(url, NavigationType.PushNewPage);
    }

    function goBack(): void {
        history.goBack();
    }

    return useMemo(() => {
        return {
            navToDocumentPage: navToDocumentPage,
            navToReportPage: navToReportPage,
            navToHomePage: navToHomePage,
            navToAddDocsPage: navToAddDocs,
            navToBrowseSamplesPage: navToBrowseSamplesPage,
            navToDocSetPage: navToDocSetPage,
            getDeepLinkToDocSetPage: getDeepLinkToDocSetPage,
            navToUnassignedFiles: navToUnassignedFiles,
            navToErrorFiles: navToErrorFiles,
            navToLocalDocs: navToLocalDocs,
            navToRemoteDocs: navToRemoteDocsPage,
            navToEmptyContentCoachmark: navToEmptyContentCoachmark,
            navToOrganizationPage: navToOrganizationPage,
            getUrlForDocumentDeeplink: getUrlForDocumentDeeplink,
            navToWorkPathPage: navToWorkPathPage,
            getWorkPathPageUrl: getWorkPathPageUrl,
            navToNewWorkPathPage: navToNewWorkPathPage,
            navToChatPage: navToChatPage,
            navToCreateDocsLandingPage: navToCreateDocsLandingPage,
            navToUnderstandDocsLandingPage: navToUnderstandDocsLandingPage,
            navToWorkFlowLandingPage: navToWorkFlowLandingPage,
            navToAccountPage: navToAccountPage,
            navToSubscriptionUpgradePage: navToSubscriptionUpgradePage,
            navToWordTaskpane: navToWordTaskpane,
            navToDeveloperHub: navToDeveloperHub,
            goBack: goBack,
            logout: logout
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [history, orgId, pageUploadDisabled, isActiveSubscriber, currentWorkPath?.id, location]);
}
